import {
    Asset,
    AttributeDefinitionOrBuiltIn,
    BaseAttributeDefinition,
    ProfileItemSection,
    ProfileItemType,
} from '../../../../types';
import React, {useContext, useMemo} from 'react';
import AttributeRowUI, {BaseAttributeRowUIProps} from './AttributeRowUI';
import {SxProps} from '@mui/material';
import {stopPropagation} from '../../../../lib/stdFuncs';
import {AttributeFormatContext} from './Format/AttributeFormatContext';
import {
    AttributeGroup,
    buildAttributesGroupedByDefinition,
} from './attributeIndex.ts';
import {
    copyToClipBoardClass,
    copyToClipBoardContainerClass,
} from './CopyAttribute.tsx';
import {
    hasDefinitionInItems,
    useProfileStore,
} from '../../../../store/profileStore.ts';
import {useTranslation} from 'react-i18next';
import {
    getBuiltInAttributeValueResolver,
    useIndexById,
} from '../../../../store/attributeDefinitionStore.ts';
import Separator from '../../../Ui/Separator.tsx';
import {Spacer} from '../../../Ui/VerticalSpacer.tsx';
import {AttributeFormat} from './types/types';
import {NO_LOCALE} from './constants.ts';
import {BuiltInAttributeEnum} from '../../Search/search.ts';

type AttributeItem = {
    id: string;
    type: ProfileItemType;
    definition?: AttributeDefinitionOrBuiltIn;
    attribute?: AttributeGroup['attribute'];
    format?: AttributeFormat;
    key?: string;
};

type Props = {
    asset: Asset;
    displayControls: boolean;
    pinnedOnly?: boolean;
} & BaseAttributeRowUIProps;

function Attributes({
    asset,
    displayControls,
    pinnedOnly,
    assetAnnotationsRef,
}: Props) {
    const {t} = useTranslation();
    const formatContext = useContext(AttributeFormatContext);
    const definitionsIndex = useIndexById(true);
    const toggleDefinition = useProfileStore(s => s.toggleDefinition);
    const current = useProfileStore(s => s.current);
    const pinnedAttributes = useMemo(
        () =>
            (current?.items ?? []).filter(
                i => i.section === ProfileItemSection.Attributes
            ),
        [current]
    );

    const attributeItems = useMemo<AttributeItem[]>(() => {
        const attributeGroups = buildAttributesGroupedByDefinition(
            asset.attributes
        );

        if (pinnedAttributes.length === 0) {
            return attributeGroups.map(ag => ({
                id: ag.definition.id,
                type: ProfileItemType.Definition,
                attribute: ag.attribute,
                definition: ag.definition,
            }));
        }

        const attributeItems: AttributeItem[] = [];

        pinnedAttributes.forEach(item => {
            const props = {
                id: item.id,
                type: item.type,
                format: item.format || undefined,
            };

            if (item.type === ProfileItemType.Definition) {
                const defId = item.definition!;
                const group = attributeGroups.find(
                    g => g.definition.id === defId
                );

                if (group) {
                    attributeItems.push({
                        ...props,
                        attribute: group.attribute,
                        definition: group.definition,
                    });
                } else if (item.displayEmpty && definitionsIndex[defId]) {
                    attributeItems.push({
                        ...props,
                        definition: definitionsIndex[defId],
                    });
                }
            } else if (item.type === ProfileItemType.BuiltIn) {
                const getValueFromAsset = getBuiltInAttributeValueResolver(
                    item.key as BuiltInAttributeEnum
                );

                if (getValueFromAsset) {
                    const v = getValueFromAsset(asset);
                    const def = definitionsIndex[item.key!];
                    if (def) {
                        if (
                            (def.multiple ? v && v.length > 0 : !!v) ||
                            item.displayEmpty
                        ) {
                            attributeItems.push({
                                ...props,
                                definition: def,
                            });
                        }
                    }
                }
            } else {
                attributeItems.push({
                    ...props,
                    key: item.key,
                });
            }
        });

        if (!pinnedOnly) {
            attributeGroups
                .filter(
                    g =>
                        !hasDefinitionInItems(pinnedAttributes, g.definition.id)
                )
                .forEach(ag => {
                    attributeItems.push({
                        id: ag.definition.id,
                        type: ProfileItemType.Definition,
                        attribute: ag.attribute,
                        definition: ag.definition,
                    });
                });
        }

        return attributeItems;
    }, [pinnedAttributes, asset, t, definitionsIndex]);

    if (attributeItems.length === 0) {
        return null;
    }

    const createAttrProps = (definition: BaseAttributeDefinition) => ({
        origin: 'machine',
        locale: NO_LOCALE,
        capabilities: {},
        definition,
    });

    return (
        <div
            onDoubleClick={stopPropagation}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            className={attributesClasses.container}
        >
            {attributeItems.map(ai => {
                if (ai.type === ProfileItemType.Definition) {
                    return (
                        <AttributeRowUI
                            key={ai.id}
                            formatContext={formatContext}
                            format={ai.format}
                            attribute={ai.attribute!}
                            definition={ai.definition!}
                            displayControls={displayControls}
                            pinned={hasDefinitionInItems(
                                pinnedAttributes,
                                ai.definition!.id
                            )}
                            togglePin={toggleDefinition}
                            assetAnnotationsRef={assetAnnotationsRef}
                        />
                    );
                } else if (ai.type === ProfileItemType.BuiltIn) {
                    const definition = ai.definition!;

                    const getValueFromAsset = getBuiltInAttributeValueResolver(
                        ai.definition!.id as BuiltInAttributeEnum
                    );

                    if (getValueFromAsset) {
                        const valueFromAsset = getValueFromAsset(asset);

                        return (
                            <AttributeRowUI
                                key={ai.id}
                                formatContext={formatContext}
                                format={ai.format}
                                attribute={
                                    definition.multiple
                                        ? valueFromAsset.map((v: any) => ({
                                              ...createAttrProps(definition),
                                              value: v,
                                          }))
                                        : {
                                              ...createAttrProps(definition),
                                              value: valueFromAsset,
                                          }
                                }
                                definition={ai.definition!}
                                displayControls={displayControls}
                                pinned={hasDefinitionInItems(
                                    pinnedAttributes,
                                    ai.definition!.id
                                )}
                                togglePin={toggleDefinition}
                                assetAnnotationsRef={assetAnnotationsRef}
                            />
                        );
                    }
                } else if (ai.type === ProfileItemType.Divider) {
                    return <Separator key={ai.id}>{ai.key!}</Separator>;
                } else if (ai.type === ProfileItemType.Spacer) {
                    return <Spacer key={ai.id} />;
                }

                return null;
            })}
        </div>
    );
}

export default React.memo(Attributes) as typeof Attributes;

export const attributesClasses = {
    container: 'attr-container',
    controls: 'attr-ctls',
    name: 'attr-name',
    val: 'attr-val',
    list: 'attr-ul',
};

export function attributesSx(): SxProps {
    return {
        [`.${attributesClasses.name}`]: {
            fontWeight: 100,
            fontSize: 13,
            my: 0.5,
        },
        [`.${attributesClasses.controls}`]: {
            'display': 'inline-block',
            'ml': 1,
            'my': -1,
            '.MuiSvgIcon-root': {
                fontSize: 13,
            },
            '.MuiButtonBase-root + .MuiButtonBase-root': {
                ml: 1,
            },
        },
        [`.${attributesClasses.val}`]: {
            'mb': 1,
            'fontSize': 14,
            '.MuiSvgIcon-root': {
                fontSize: 13,
            },
        },
        [`.${attributesClasses.list}`]: {
            m: 0,
            pl: 1,
        },
        [`.${copyToClipBoardContainerClass} .${copyToClipBoardClass}`]: {
            visibility: 'hidden',
            ml: 2,
        },
        [`.${copyToClipBoardContainerClass}:hover .${copyToClipBoardClass}`]: {
            visibility: 'visible',
        },
    };
}
